import type { ReactNode } from 'react'
import { Badge } from '../../../components/Badge/Badge'
import { CodeBadge } from '../../../components/CodeBadge/CodeBadge'
import { formatDate } from '../../../helpers/formatDate'
import { deriveConfigScope } from '../../../helpers/deriveConfigScope'
import { ConfigScope } from '../../../types'
import type { ConfigDTO, EditRowDef } from '../../../types'

// `tagsEditor` is the controlled tags editor from ConfigEditPage; it renders in
// the value cell of the "Tags" row, placed just before Created.
export function buildConfigEditRows(
  config: ConfigDTO,
  venueOptions: { label: string; value: string }[],
  tagsEditor: ReactNode
): EditRowDef[] {
  const scope = deriveConfigScope(config)

  const scopeRows = buildScopeEditRows(scope, config, venueOptions)

  return [
    {
      mode: 'readonly',
      label: 'ID',
      value: <span className="font-monospace small">{config.id}</span>,
    },
    {
      mode: 'readonly',
      label: 'Scope',
      value: scope ? <Badge color="blue">{scope}</Badge> : null,
    },
    ...scopeRows,
    {
      mode: 'readonly',
      label: 'Key',
      value: <span className="fw-medium">{config.configKey}</span>,
    },
    {
      mode: 'editable',
      field: {
        name: 'configValue',
        label: 'Value',
        type: 'text',
        required: true,
      },
      initialValue: config.configValue,
    },
    {
      mode: 'readonly',
      label: 'Type',
      value: <Badge color="amber">{config.configType}</Badge>,
    },
    {
      mode: 'editable',
      field: {
        name: 'visibleToFrontend',
        label: 'Visible To Frontend',
        type: 'checkbox',
      },
      initialValue: config.visibleToFrontend,
    },
    { mode: 'readonly', label: 'Tags', value: tagsEditor },
    { mode: 'readonly', label: 'Created', value: formatDate(config.createdAt) },
    { mode: 'readonly', label: 'Updated', value: formatDate(config.updatedAt) },
  ]
}

function buildScopeEditRows(
  scope: ConfigScope | null,
  config: ConfigDTO,
  venueOptions: { label: string; value: string }[]
): EditRowDef[] {
  if (!scope) {
    return []
  }

  if (scope === ConfigScope.IspConfig) {
    return [
      {
        mode: 'readonly',
        label: 'ISP',
        value: <CodeBadge code={config.inventorySystemProfileCode} />,
      },
    ]
  }

  if (scope === ConfigScope.TenantConfig) {
    return [
      {
        mode: 'readonly',
        label: 'Tenant',
        value: <CodeBadge code={config.tenantCode} />,
      },
    ]
  }

  if (scope === ConfigScope.TenantIspConfig) {
    return [
      {
        mode: 'readonly',
        label: 'Tenant',
        value: <CodeBadge code={config.tenantCode} />,
      },
      {
        mode: 'readonly',
        label: 'ISP',
        value: <CodeBadge code={config.inventorySystemProfileCode} />,
      },
    ]
  }

  return [
    {
      mode: 'readonly',
      label: 'Tenant',
      value: <CodeBadge code={config.tenantCode} />,
    },
    {
      mode: 'editable',
      field: {
        name: 'venueId',
        label: 'Venue',
        type: 'select',
        searchable: true,
        options: venueOptions,
        required: true,
      },
      initialValue: config.venueId,
    },
  ]
}
